import { createContext, useState, useContext, useCallback, useMemo, ReactNode } from 'react';

interface MonthContextType {
  selectedDate: Date;
  selectedMonth: number;
  selectedYear: number;
  setSelectedDate: (date: Date) => void;
  setMonth: (month: number, year: number) => void;
  goToMonth: (date: Date) => void;
}

interface MonthProviderProps {
  children: ReactNode;
}

// ES6+ Context nativo
const MonthContext = createContext<MonthContextType | null>(null);

// Provider usando ES6+ features nativas
export const MonthProvider = ({ children }: MonthProviderProps) => {
  const [selectedDate, setSelectedDate] = useState(() => new Date());

  // Computed values usando ES6+ getters
  const selectedMonth = selectedDate.getMonth(); // 0-11 para consistência com Date.getMonth()
  const selectedYear = selectedDate.getFullYear();

  // Methods usando ES6+ arrow functions
  const setMonth = useCallback((month: number, year: number) => {
    setSelectedDate(new Date(year, month, 1)); // month já está em 0-11
  }, []);

  const goToMonth = useCallback((date: Date) => {
    setSelectedDate(new Date(date.getFullYear(), date.getMonth(), 1));
  }, []);

  // Context value com useMemo nativo
  const contextValue = useMemo(() => ({
    selectedDate,
    selectedMonth,
    selectedYear,
    setSelectedDate,
    setMonth,
    goToMonth
  }), [selectedDate, selectedMonth, selectedYear, setMonth, goToMonth]);

  return (
    <MonthContext.Provider value={contextValue}>
      {children}
    </MonthContext.Provider>
  );
};

// Hook customizado ES6+
export const useMonth = () => {
  const context = useContext(MonthContext);
  if (!context) {
    throw new Error('useMonth deve ser usado dentro de MonthProvider');
  }
  return context;
};
